import React, { useCallback, useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import Toast from 'components/Toast';
import Button from 'components/Button';
import { useActiveCompanyStore } from 'stores/activeCompanyStore';
import {
  createCompanyApiKey,
  listCompanyApiKeys,
  revokeCompanyApiKey,
  type CompanyApiKeyListItem,
} from 'services/companyApiKeys';
import styles from './ApiKeys.module.scss';

const ApiKeys: React.FC = () => {
  const activeCompanyId = useActiveCompanyStore((s) => s.activeCompanyId);
  const [rows, setRows] = useState<CompanyApiKeyListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [plainKey, setPlainKey] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  const load = useCallback(async () => {
    if (!activeCompanyId) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const list = await listCompanyApiKeys(activeCompanyId);
      setRows(list);
    } catch (e) {
      setToast({
        message: e instanceof Error ? e.message : 'Failed to load API keys.',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [activeCompanyId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCreate = async () => {
    if (!activeCompanyId) return;
    setCreating(true);
    setPlainKey(null);
    try {
      const res = await createCompanyApiKey(
        activeCompanyId,
        name.trim() || undefined,
      );
      setPlainKey(res.plainKey);
      setToast({
        message: 'Key created. Copy it now — it will not be shown again.',
        type: 'success',
      });
      setName('');
      await load();
    } catch (e) {
      setToast({
        message: e instanceof Error ? e.message : 'Failed to create key.',
        type: 'error',
      });
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id: string) => {
    if (!activeCompanyId) return;
    try {
      await revokeCompanyApiKey(activeCompanyId, id);
      setToast({ message: 'Key revoked.', type: 'success' });
      await load();
    } catch (e) {
      setToast({
        message: e instanceof Error ? e.message : 'Failed to revoke key.',
        type: 'error',
      });
    }
  };

  return (
    <div className={styles.container}>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <h1 className={styles.title}>API Keys</h1>
      <p className={styles.hint}>
        Manage company API keys for machine-to-machine requests. Use header{' '}
        <code>X-Company-Api-Key</code> for enabled endpoints.
      </p>
      {!loading && rows.length > 0 && activeCompanyId && (
        <p className={styles.companyIdHint}>
          Company ID: <code>{activeCompanyId}</code>
        </p>
      )}

      {plainKey && (
        <div className={styles.plainBox}>
          <div className={styles.plainLabel}>New key (copy now)</div>
          <code className={styles.plainCode}>{plainKey}</code>
        </div>
      )}

      <div className={styles.createRow}>
        <Form.Control
          type="text"
          placeholder="Optional label (e.g. Landing website)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={styles.nameInput}
        />
        <Button
          type="button"
          onClick={() => void handleCreate()}
          disabled={creating || !activeCompanyId}
        >
          {creating ? 'Creating...' : 'Generate key'}
        </Button>
      </div>

      {loading ? (
        <p className={styles.muted}>Loading...</p>
      ) : (
        <ul className={styles.list}>
          {rows.map((row) => (
            <li key={row.id} className={styles.row}>
              <div>
                <div className={styles.keyLine}>
                  <code>{row.keyPrefix}...</code>
                  {row.name && (
                    <span className={styles.nameTag}>{row.name}</span>
                  )}
                </div>
                <div className={styles.meta}>
                  Created {new Date(row.createdAt).toLocaleString()}
                  {row.revokedAt && (
                    <span className={styles.revoked}>
                      {' '}
                      - Revoked {new Date(row.revokedAt).toLocaleString()}
                    </span>
                  )}
                </div>
              </div>
              {!row.revokedAt && (
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  onClick={() => void handleRevoke(row.id)}
                >
                  Revoke
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ApiKeys;
